// @owners { team: patients-team }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchInsuranceSucceeded, fetchInsurancesFailed, fetchInsurancesSucceeded } from '~shared/actions/insurances';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { useDispatchShared } from '~shared/store';
import { type APIError, type ReduxDispatchShared } from '~shared/types';
import { type Insurance } from '~shared/types/clients';

type FetchAllInsurancesResponse = Insurance[] & { error?: APIError };

export const insurances = createQueryKeys('insurances', {
  fetchAll: ({ dispatch }: { dispatch: ReduxDispatchShared }) => ({
    queryKey: ['fetchAll'],
    queryFn: async () => {
      const response: FetchAllInsurancesResponse = await get('/insurances');
      // FIXME: need to keep redux insurances in sync until all related selectors removed
      // i.e. getHasInsurances
      if (response.error) {
        dispatch(fetchInsurancesFailed(response.error));
        throw new Error(getErrorMessageFromResponse({ error: response.error }));
      } else {
        dispatch(fetchInsurancesSucceeded(response));
      }
      return response;
    },
  }),
  fetchOne: ({ dispatch, insuranceID }: { dispatch: ReduxDispatchShared; insuranceID: number | null | undefined }) => ({
    queryKey: [{ insuranceID }],
    queryFn: async () => {
      const response = await get(`/insurances/${insuranceID}`);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse({ error: response.error }));
      } else {
        // FIXME: keep redux insurances in sync until remaining selectors removed
        // i.e. getHasInsurances
        dispatch(fetchInsuranceSucceeded(response));
        return response as Insurance;
      }
    },
  }),
});

const useInsuranceByIDFromCache = (insuranceID: number | undefined | null) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const cachedInsurances: Insurance[] = queryClient.getQueryData(insurances.fetchAll({ dispatch }).queryKey) ?? [];
  return cachedInsurances.find((insurance) => insurance.id === insuranceID);
};

/**
 * Fetch insurance by id for a patient
 * @param insuranceID
 * @returns insurance
 */
export const useGetInsuranceByID = (insuranceID: number | undefined | null) => {
  const cachedInsurance = useInsuranceByIDFromCache(insuranceID);
  const dispatch = useDispatchShared();
  const { data, isLoading, isSuccess, error } = useQuery({
    ...insurances.fetchOne({ dispatch, insuranceID }),
    enabled: !cachedInsurance && !!insuranceID,
  });

  return {
    insurance: cachedInsurance ?? data,
    isLoading,
    error,
    isSuccess,
  };
};

/**
 * Fetch all insurances for a patient, including family member insurances
 * @returns Array of insurances
 */
export const useGetInsurances = ({ enabled } = { enabled: true }) => {
  const dispatch = useDispatchShared();
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    ...insurances.fetchAll({ dispatch }),
    enabled,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  const fetchedInsurances = data ?? [];

  return {
    insurances: fetchedInsurances,
    error,
    isLoading,
    isSuccess,
    hasActiveInsurances: fetchedInsurances.some((insurance) => insurance.is_active),
    hasInsurance: fetchedInsurances.length > 0,
    hasGovernmentInsurance: fetchedInsurances.some(({ is_government_insurance }) => !!is_government_insurance),
    refetch,
  };
};

/**
 * Fetch all insurances for a given patient
 * @param userID patient's user ID
 * @returns  Array of insurances
 */
export const useGetInsurancesByUserID = ({ userID }: { userID: number | null | undefined }) => {
  const getInsuranceByUserID = useCallback(
    (insurances: Insurance[]) => {
      return insurances.filter(({ user_id }) => user_id === userID);
    },
    [userID],
  );

  const dispatch = useDispatchShared();
  const { data, isLoading, isSuccess, error } = useQuery({
    ...insurances.fetchAll({ dispatch }),
    select: getInsuranceByUserID,
    enabled: !!userID,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    hasActiveInsurances: (data ?? []).some((insurance) => insurance.is_active),
    hasInsurances: !!data?.length,
    insurances: data ?? [],
    isLoading,
    isSuccess,
    error,
  };
};

/**
 * Fetch all insurances found via cardfinder service
 * @returns Array of insurances
 */
export const useGetCardFinderInsurances = () => {
  const getCardFinderInsurances = useCallback((insurances: Insurance[]) => {
    return insurances.filter(({ source }) => source === 'cardfinder');
  }, []);

  const dispatch = useDispatchShared();
  const { data, isLoading, error } = useQuery({
    ...insurances.fetchAll({ dispatch }),
    select: getCardFinderInsurances,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });
  const cardFinderInsurances = data ?? [];

  return {
    insurances: cardFinderInsurances,
    hasCardFinderInsurance: cardFinderInsurances.length > 0,
    isLoading,
    error,
  };
};
